import type { FormikProps } from "formik";
import type { TFunction } from "react-i18next";
import type { getPhaseOfDateRange } from "senf-shared/index";
import type { TagProps } from "../../atoms/tag/Tag";
import type { IconName } from "../../core/icon/names";
import type {
  Question,
  QuestionValue,
  ValueTypes,
} from "./legacy/survey.types";
import type { useEditQuestion } from "./utils";

// ---------------
// Shared types
// ---------------
export type QuestionType =
  | "textResponse"
  | "numberInput"
  | "choice"
  | "choiceImage"
  | "choiceMatrix"
  | "budgetAllocation"
  | "ranking"
  | "ratingScale"
  | "ratingSlider"
  | "ratingStars"
  | "mapPoint"
  | "mapPath"
  | "mapArea";
export type StructuralPageType =
  | "intro"
  | "outro"
  | "demographics"
  | "overview"
  | "section";

export type PageType = QuestionType | StructuralPageType;

type PageTypeCategory =
  | "text"
  | "selection"
  | "rating"
  | "prioritization"
  | "map"
  | "structure"
  | "intro"
  | "outro";

export interface Condition {
  type: "equals" | "notEquals" | "none";
  uid: string;
  goToPage: string;
  response?: string;
}

export interface SurveyLogo {
  url: string;
  uid: string;
  altText?: string;
  path?: string;
}

export interface SurveyImage extends ReactGridLayout.Layout {
  url: string;
  altText?: string;
  copyright?: string;
  path?: string;
  name?: string;
}

export type TextResponseType = {
  enabled: boolean;
  label?: string | null;
  placeholder?: string | null;
  required?: boolean;
  minChars?: number;
  maxChars?: number;
};

export type LimitType = {
  enabled: boolean;
  min?: number;
  max?: number;
};

export type ValueBoolean = {
  valueType: "boolean";
  multipleChoice: LimitType;
  otherOption?: TextResponseType;
  shuffle?: boolean;
  mapResponse?: {
    placeholder?: string;
  };
};
type ValueString = {
  valueType: "string";
  limitCharacters: Pick<
    TextResponseType,
    "enabled" | "minChars" | "maxChars" | "required" | "placeholder"
  >;
};
type ValueNumber = { valueType: "number"; limit?: LimitType };
type ValueNone = { valueType: "none" };

export type ValueTypeMap = {
  boolean: ValueBoolean;
  string: ValueString;
  number: ValueNumber;
  none: ValueNone;
};

// common settings on every page
type QuestionBase = {
  required: boolean;
  shuffle?: boolean;
};

export type SettingsMapType<V extends keyof ValueTypeMap = keyof ValueTypeMap> =
  QuestionBase & {
    mapResponse: { limit?: LimitType };
    shuffle?: undefined;
  } & ValueTypeMap[V];

export type MapSettings = {
  mapId?: string;
  settings?:
    | {
        center?: [number, number];
        pitch?: number;
        bearing?: number;
        zoom?: number;
        height?: undefined;
      }
    | {
        center?: [number, number];
        pitch?: number;
        bearing?: number;
        zoom?: undefined;
        height?: number;
      };
};

export type EvaluationTableColumn = {
  type: "tags" | "choices" | "description";
  label: string;
  value: string;
  sortable?: boolean;
  aiGenerated?: boolean;
  values: {
    name: string;
    value: string; // resolved result columnId?
    color: string;
    label: string;
  }[];
};

export type EvaluationSettings = {
  wordCloud?: {
    text: string;
    value: number;
    color: string | null;
  }[];
  tableColumns?: EvaluationTableColumn[];
};

// ---------------
// Page‑type overrides
// ---------------

type PageTypeSettingsOverrides<
  V extends keyof ValueTypeMap = keyof ValueTypeMap,
> = Record<PageType, unknown> & {
  textResponse: QuestionBase & {
    limitCharacters: Pick<
      TextResponseType,
      "enabled" | "minChars" | "maxChars" | "required"
    >;
  };
  numberInput: QuestionBase & { limit?: LimitType };
  choice: QuestionBase & {
    multipleChoice: LimitType;
    otherOption?: TextResponseType;
  };
  choiceImage: QuestionBase & {
    multipleChoice: LimitType;
    otherOption?: TextResponseType;
  };
  choiceMatrix: QuestionBase & { multipleChoice: LimitType };
  ranking: QuestionBase & { limit?: LimitType };
  budgetAllocation: QuestionBase & { budget: number };
  ratingScale: QuestionBase & {
    monochrome?: boolean;
    textResponse?: TextResponseType;
  };
  ratingSlider: QuestionBase & {
    numeric: boolean;
    steps: number;
    textResponse?: TextResponseType;
  };
  ratingStars: QuestionBase & { textResponse?: TextResponseType };
  mapPoint: SettingsMapType<V>;
  mapPath: SettingsMapType<V>;
  mapArea: SettingsMapType<V>;
  outro: {
    buttons: {
      infoPage?: boolean;
      dialogMap?: boolean;
      socialMedia?: boolean;
      custom?: { label: string; url: string; uid: string }[];
    };
  };
};

// ---------------
// Build the discriminated union
// ---------------

// map each page‑type to its full settings
export type PageSettingsMap<
  T extends PageType,
  V extends keyof ValueTypeMap = keyof ValueTypeMap,
> = T extends keyof PageTypeSettingsOverrides
  ? {
      settings: {
        [K in keyof PageTypeSettingsOverrides]: PageTypeSettingsOverrides<V>[K];
      }[T];
    }
  : {
      settings?: Record<PageType, unknown>[T];
    };
// utility for rows/columns
export type InputCell = {
  uid: string;
  label: string | undefined;
  subLabel?: string | undefined;
  index?: number;
};

type BudgetAllocationCell = { points?: number };
type ImageInputCell = { url?: string; altText?: string; copyright?: string };
type TextInputCell = { placeholder?: string };
type RatingCell = { minLabel?: string; maxLabel?: string };
type Row = Record<PageType, unknown> & {
  textResponse: TextInputCell;
  ratingScale: RatingCell;
  ratingSlider: RatingCell;
};
type Column = Record<PageType, unknown> & {
  choiceImage: ImageInputCell;
  budgetAllocation: BudgetAllocationCell;
};

export type InputRow<T extends PageType> = InputCell & Row[T];
export type InputColumn<T extends PageType> = InputCell & Column[T];
export type Options<T extends PageType> = T extends QuestionType
  ? {
      options: {
        rows: InputRow<T>[];
        columns: InputColumn<T>[];
        // order?: { rows?: number[]; columns?: number[] }; // @todo: what is order?
      };
    }
  : {
      options?: never;
    };

// logo & image settings on the page
export type LogoSettings = { items: SurveyLogo[]; size?: "sm" | "md" | "lg" };
export type ImageSettings = {
  items: SurveyImage[];
  format?: "fill" | "fit";
};

// ---------------
// Final Page union
// ---------------

export type PageOf<
  T extends PageType,
  V extends keyof ValueTypeMap = keyof ValueTypeMap,
> = {
  type: T;
  uid: string;
  index?: number;
  title?: string;
  subtitle?: string;

  images?: ImageSettings;
  logos?: LogoSettings;
  map?: MapSettings;

  conditions?: Condition[];
  evaluation?: EvaluationSettings;
} & PageSettingsMap<T, V> &
  Options<T>;

export type Page<
  T extends PageType | undefined = undefined,
  V extends keyof ValueTypeMap = keyof ValueTypeMap,
> = T extends PageType
  ? PageOf<T, V>
  : {
      [K in keyof PageTypeSettingsOverrides]: PageOf<K, V>;
    }[keyof PageTypeSettingsOverrides];

// Example of a properly structured mapPoint page
// const a: Page<"mapPoint"> = {
//   type: "mapPoint",
//   uid: "example-uid",
//   index: 0,
//   title: "Example Map Point",
//   subtitle: "Example subtitle",
//   options: {
//     rows: [],
//     columns: [],
//   },
//   settings: {
//     required: true,
//     mapResponse: {
//       limit: {
//         enabled: false,
//       },
//     },
//     valueType: "boolean",
//     multipleChoice: {
//       enabled: false,
//     },
//   },
//   map: {
//     center: [0, 0],
//     zoom: 1,
//     pitch: 0,
//     bearing: 0,
//   },
// };

export interface Survey {
  title: string;
  surveyId: string;
  organizationId: string;
  projectId: string;
  pages: Page[];

  createdAt: string;

  endDate: string;
  startDate: string;

  callToParticipate: string;
  settings: {
    publicAccess: boolean;
  };
}

export interface LocalSurvey extends Survey {
  phase?: ReturnType<typeof getPhaseOfDateRange>;
  formattedStartDate?: string; // @todo: what is formattedStartDate?
  formattedEndDate?: string; // @todo: what is formattedEndDate?
}

type ConfigDefaults = {
  rows?: number;
  minRows?: number;
  maxRows?: number;
  columns?: number;
  minColumns?: number;
  maxColumns?: number;
  allowEmpty?: readonly ["rows" | "columns"];
};
export interface PageTypeConfigItem<K extends PageType> {
  leadingIcon: IconName;
  swapGroup?: string; // @todo: what is swapGroup?
  category?: PageTypeCategory;
  color: TagProps["color"];
  defaults?:
    | ConfigDefaults
    | ((valueType?: keyof ValueTypeMap) => ConfigDefaults);
  sidebarTitle?: string;
  sidebarNumber?: string;
  hideInSelection?: boolean;
  structuralPage?: boolean;
  sortable?: boolean;
  logic?: boolean;
}

type PageTypeConfig = {
  [K in PageType]: PageTypeConfigItem<K>;
};

export const pageTypeConfig: PageTypeConfig = {
  textResponse: {
    leadingIcon: "edit",
    category: "text",
    color: "teal-400",
    defaults: {
      rows: 1,
    },
  },
  numberInput: {
    leadingIcon: "number",
    category: "text",
    color: "teal-500",
    defaults: {
      rows: 1,
    },
  },
  choice: {
    leadingIcon: "checkmark-xl",
    category: "selection",
    color: "orange-400",
    defaults: {
      minColumns: 1,
      columns: 2,
    },
    logic: true,
  },
  choiceMatrix: {
    leadingIcon: "matrix",
    category: "selection",
    color: "orange-500",
    defaults: {
      rows: 2,
      minRows: 2,
      columns: 2,
      minColumns: 2,
      allowEmpty: ["columns"],
    },
  },
  choiceImage: {
    leadingIcon: "image",
    category: "selection",
    color: "orange-600",
    defaults: {
      minColumns: 1,
      columns: 2,
    },
    logic: true,
  },
  ratingScale: {
    leadingIcon: "nps",
    category: "rating",
    color: "yellow-300",
    defaults: {
      rows: 1,
      columns: 3,
      minColumns: 2,
      maxColumns: 10,
    },
  },
  ratingStars: {
    leadingIcon: "star-fill",
    category: "rating",
    color: "yellow-400",
    defaults: {
      rows: 1,
      columns: 5,
      maxColumns: 5,
    },
  },
  ratingSlider: {
    leadingIcon: "slider",
    category: "rating",
    color: "yellow-500",
    defaults: {
      rows: 1,
      columns: 5,
      minColumns: 2,
      maxColumns: 100,
    },
  },
  ranking: {
    leadingIcon: "ranking",
    category: "prioritization",
    color: "purple-400",
    defaults: {
      minColumns: 2,
      columns: 2,
    },
  },
  budgetAllocation: {
    leadingIcon: "participatory-budget",
    category: "prioritization",
    color: "purple-500",
    defaults: {
      minColumns: 2,
      columns: 2,
    },
  },
  mapPoint: {
    leadingIcon: "pin-alt",
    category: "map",
    color: "green-300",
    defaults: (valueType) =>
      valueType === "boolean" || valueType === "none"
        ? {
            minColumns: 2,
            columns: 2,
          }
        : {
            minRows: 2,
            rows: 2,
          },
  },
  mapPath: {
    leadingIcon: "path",
    category: "map",
    color: "green-400",
    defaults: (valueType) =>
      valueType === "boolean" || valueType === "none"
        ? {
            minColumns: 2,
            columns: 2,
          }
        : {
            minRows: 2,
            rows: 2,
          },
  },
  mapArea: {
    leadingIcon: "area",
    category: "map",
    color: "green-500",
    defaults: (valueType) =>
      valueType === "boolean" || valueType === "none"
        ? {
            minColumns: 2,
            columns: 2,
          }
        : {
            minRows: 2,
            rows: 2,
          },
  },
  overview: {
    sidebarNumber: "",
    leadingIcon: "statistics",
    color: "neutral-400",
    hideInSelection: true,
    structuralPage: true,
  },
  intro: {
    sidebarNumber: "",
    leadingIcon: "first-screen-horizontal",
    category: "intro",
    color: "neutral-400",
    hideInSelection: true,
    structuralPage: true,
    sortable: false,
  },
  outro: {
    sidebarNumber: "",
    leadingIcon: "last-screen-horizontal",
    category: "outro",
    color: "neutral-400",
    hideInSelection: true,
    structuralPage: true,
    sortable: false,
  },
  section: {
    leadingIcon: "paragraph",
    category: "structure",
    color: "neutral-400",
    structuralPage: true,
  },
  demographics: {
    leadingIcon: "user-fill",
    color: "neutral-400",
    hideInSelection: true,
    structuralPage: true,
  },
};

// 3) build a discriminated union over the entries
export type PageTypeConfigEntry = {
  [K in PageType]: { type: K } & PageTypeConfig[K];
}[PageType];

export const pageTypeSettings = {
  textResponse: {
    minChars: null as null | number,
    maxChars: null as null | number,
  },
  ratingScale: {
    monochrome: false as boolean,
  },
} as const satisfies Partial<Record<PageType, Record<string, unknown>>>;

interface LocalizedPageTypeConfig<T extends PageType>
  extends PageTypeConfigItem<T> {
  value: T;
  text: string;
  categoryLabel: string;
}

export const pageTypes = ({
  t,
}: {
  t?: TFunction<"translation", undefined>;
} = {}) => {
  const textMap = {
    textResponse: `${t?.("survey:pageTypes.textResponse")}`,
    numberInput: t?.("survey:pageTypes.numberInput"),
    choice: t?.("survey:pageTypes.choice"),
    choiceImage: t?.("survey:pageTypes.choiceImage"),
    choiceMatrix: t?.("survey:pageTypes.choiceMatrix"),
    budgetAllocation: t?.("survey:pageTypes.participatoryBudget"),
    ranking: t?.("survey:pageTypes.ranking"),
    ratingScale: t?.("survey:pageTypes.ratingScale"),
    ratingSlider: t?.("survey:pageTypes.ratingSlider"),
    ratingStars: t?.("survey:pageTypes.ratingStars"),
    mapPoint: t?.("survey:pageTypes.setPoints"),
    mapPath: t?.("survey:pageTypes.setRoutes"),
    mapArea: t?.("survey:pageTypes.setAreas"),
    section: t?.("survey:pageTypes.section"),
    overview: t?.("survey:pageTypes.overview"),
    intro: t?.("survey:pageTypes.intro"),
    outro: t?.("survey:pageTypes.outro"),
    demographics: t?.("survey:pageTypes.demographicData"),
  };

  const categoryLabels = {
    text: t?.("global:text"),
    selection: t?.("global:selection"),
    rating: t?.("global:rating"),
    priority: t?.("global:priorization"),
    map: t?.("global:map"),
    structure: t?.("global:structure"),
  };

  return Object.entries(pageTypeConfig).map(
    ([key, value]) =>
      ({
        ...value,
        categoryLabel:
          "category" in value && value.category
            ? categoryLabels[value.category]
            : "",
        value: key,
        text: textMap[key],
      }) as LocalizedPageTypeConfig<PageType>,
  );
};

// const a: PageTypeSettings<"textResponse"> = {
//   minChars: 1,
//   maxChars: 100,
// };

export const newPageType: Record<Question["qtype"], PageType> = {
  text: "textResponse",
  number: "numberInput",
  radio: "choice",
  imageChoice: "choiceImage",
  radioMatrix: "choiceMatrix",
  budget: "budgetAllocation",
  ranking: "ranking",
  ratingScale: "ratingScale",
  slider: "ratingSlider",
  stars: "ratingStars",
  point: "mapPoint",
  path: "mapPath",
  area: "mapArea",
  intro: "intro",
  outro: "outro",
  demographics: "demographics",
  overview: "overview",
  section: "section",
};

export interface QuestionBaseProps<P extends PageType>
  extends ReturnType<typeof useEditQuestion> {
  // general props
  mode?: "edit" | "display";
  wrapperRef?: React.RefObject<HTMLDivElement>;
  page: Page<P>;
  formikValues: Page<P>;
  formik: FormikProps<Page<P>>;
  // display props
  value: ValueTypes;
  onChange?: (props: Partial<QuestionValue>) => void;
  // edit props
  defaultTextTitle?: string;
  defaultOtherTitle?: string;
}
